import { z } from 'zod';
import { MappingProposalResponseSchema } from '../blueprints/admin-wire';
import { NoteRecordKindSchema } from '../flow/notes';
import { WIZARD_STEP_IDS } from '../blueprints/authoring/wizard-steps';

/**
 * Zod schemas for the app-home SSE stream (/api/v4/stream) — the single
 * source of truth shared by the server emitter (SseRegistry.emit) and the web
 * consumer (sseStore). The web path
 * web/app-home/src/lib/app-home-sse-schemas.ts re-exports this module.
 *
 * Trust boundary: sseStore.ts JSON.parses each event's data off the
 * wire. Validation happens there, not downstream. Consumer types are
 * inferred from these schemas so drift is visible at the compiler level.
 *
 * Producer shapes cataloged in plans/2026-04-22-app-home-sse-shapes.md .
 */

export const ToastPayloadSchema = z.object({
  message: z.string(),
  level: z.enum(['info', 'warn', 'error']).optional(),
  /** Present on reauth toasts so the client can render a reconnect action. */
  oauthUrl: z.string().optional(),
  /** Stable code (e.g. 'SF_REAUTH_REQUIRED') carried alongside oauthUrl. */
  code: z.string().optional(),
});

export const ArtifactCreatedPayloadSchema = z.object({
  uuid: z.string(),
});

export const ArtifactDeletedPayloadSchema = z.object({
  uuid: z.string(),
});

/**
 * The backend spreads arbitrary changed-field data in alongside uuid
 * (`emitArtifactUpdated` in src/services/sse/sse-emit.ts). Consumers that need specific
 * fields validate downstream.
 */
export const ArtifactUpdatedPayloadSchema = z
  .object({ uuid: z.string() })
  .catchall(z.unknown());

/**
 * Backend emits `{ reason, ...extras }` where extras is caller-supplied.
 *
 * Known reasons carry typed extras; unknown/legacy reasons fall through to the
 * generic branch which preserves arbitrary extras as `unknown`. Producer call
 * sites are enumerated at `src/services/sse/sse-emit.ts` (InvalidateReason).
 *
 * Narrowing at the consumer is via `reason === 'execution_artifact_received'`
 * etc. — the discriminator is `reason`, but we can't use z.discriminatedUnion
 * because a) `reason` is optional on the generic branch and b) the generic
 * branch accepts any reason string, not a literal.
 */
export const ExecutionArtifactReceivedInvalidatePayloadSchema = z.object({
  reason: z.literal('execution_artifact_received'),
  actionName: z.string().optional(),
  correlationId: z.string().optional(),
  artifactCount: z.number().optional(),
  executionId: z.union([z.string(), z.number()]).optional(),
  expectedArtifactCount: z.number().optional(),
});

/**
 * Emitted by `emitLoopSuggestionsReady` when a background loop-suggestion
 * generation for one account settles, successful or not (keystone stores rows
 * per loop, so a failed run can still have stored some). The client refetches
 * that account's `GET /api/v4/open-loops/suggestions`.
 */
export const LoopSuggestionsReadyInvalidatePayloadSchema = z.object({
  reason: z.literal('loop_suggestions_ready'),
  salesforceAccountId: z.string(),
});

export const GenericInvalidatePayloadSchema = z
  .object({
    reason: z.string().optional(),
  })
  .catchall(z.unknown());

export const InvalidatePayloadSchema = z.union([
  ExecutionArtifactReceivedInvalidatePayloadSchema,
  LoopSuggestionsReadyInvalidatePayloadSchema,
  GenericInvalidatePayloadSchema,
]);

/**
 * Blueprint studio truth channel (plan §3 "Protocol: projection + one
 * idempotent cue blob"). Emitted by `emitBlueprintDraftUpdated`
 * (src/services/sse/sse-emit.ts) on every draft write.
 *
 * Pure invalidation — never carries content. The studio refetches
 * `GET /:kind` and re-renders, so a missed event self-heals on the next
 * fetch.
 *
 * Unlike the other payloads on this stream, this one is validated with a
 * HARD gate in sseStore (invalid → dropped, not passed through): the
 * studio's never-flash-broken invariant means a malformed frame must not
 * become a render input.
 */
export const BlueprintDraftUpdatedPayloadSchema = z.object({
  kind: z.string(),
  seq: z.number(),
});
export type BlueprintDraftUpdatedPayload = z.infer<typeof BlueprintDraftUpdatedPayloadSchema>;

const StudioCueChecklistItemPayloadSchema = z.object({
  id: z.string(),
  label: z.string(),
  state: z.enum(['pending', 'active', 'done', 'failed']),
  /** `/test` results and publish-422 `didYouMean` text ride here (plan §3 v1 extension). */
  detail: z.string().optional(),
  /** Deixis ref the badge anchors to, e.g. `@slot:status`. */
  ref: z.string().optional(),
});

/**
 * Blueprint studio presentation channel (plan §3). Emitted verbatim by
 * `emitStudioCue` (src/services/sse/sse-emit.ts) from the server-side
 * `StudioCueSchema`-validated body of `POST /:kind/cue`.
 *
 * The single source of truth for the cue's keys. The backend request schema
 * `StudioCueSchema` (src/api/v4/admin-blueprints.ts) is DERIVED from this one
 * via `.extend`, restating only its tightenings: non-empty ids, and
 * `proposal.blueprint` typed as the full draft body (opaque here — the
 * studio's render path re-validates it against the real blueprint schema
 * before rendering). Add a new cue key HERE only; the derived
 * schema picks it up, so Zod cannot strip it.
 *
 * Each cue is the full presentation state: idempotent, last-write-wins, no
 * ordering or expiry semantics.
 */
export const StudioCuePayloadSchema = z.object({
  sessionId: z.string(),
  kind: z.string(),
  /** Monotonic per session; the viewer drops stale cues. */
  seq: z.number(),
  // The original 5 studio scenes plus the 8 wizard step ids (U3, bd-25shv) —
  // additive only. StudioCueRail's SceneStrip falls every unrecognized value
  // to "upcoming" (currentIndex === -1), so a wizard-step cue reaching the
  // Studio rail renders gracefully rather than crashing.
  step: z.enum([
    'connect', 'draft', 'refine', 'test', 'publish',
    'start', 'identity', 'cardType', 'mapping', 'surfaces', 'quickActions', 'detailPanel', 'review',
  ]),
  checklist: z.array(StudioCueChecklistItemPayloadSchema),
  focus: z
    .object({
      target: z.enum(['card', 'slot', 'action', 'detailSection', 'table']),
      id: z.string(),
    })
    .nullable(),
  narration: z.string().nullable(),
  proposal: z
    .object({
      id: z.string(),
      // Optional as of U3 (bd-25shv): an identity-only proposal (U11) carries
      // no blueprint at all. Every consumer of `proposal.blueprint` must
      // tolerate `undefined` — identity rides the sibling `identity` field,
      // never merged into this opaque blob.
      blueprint: z.record(z.string(), z.unknown()).optional(),
      note: z.string(),
      // Per-tab, best-effort freshness hint the wizard advertises via its
      // '@wizard:state' cue (U5). Not a session nonce — two tabs on the same
      // kind can hold numerically equal revisions (OQ4, accepted risk).
      baseRevision: z.number().optional(),
      // When true, the wizard applies this proposal without a confirm step (U11).
      autoApply: z.boolean().optional(),
      // Identity-only proposal payload (U11). `source` is a raw string the
      // wizard resolves on apply (not pre-resolved).
      identity: z
        .object({
          kind: z.string().optional(),
          displayName: z.string().optional(),
          source: z.string().optional(),
          // The agent supplies this explicitly (it already knows the type
          // from having called resolve-table/resolve-workflow itself) — the
          // wizard does not sniff/infer it from the URL. Omitted defaults to
          // 'clayTable' (today's behavior).
          sourceType: z.enum(['clayTable', 'clayWorkflow']).optional(),
          // Matters for workflow sources only: without it, `connectIfNeeded`
          // falls back to the graph's heuristic default node, which may not
          // be the node the agent's downstream mapping proposals were
          // computed against.
          nodeId: z.string().optional(),
        })
        .optional(),
      // Mapping-step evidence payload (plan 2026-08-03-002 U7): the exact
      // classifier contract `POST /:kind/mapping-proposal` already returns, so
      // an agent cue can light up Mapping's per-slot evidence UI (candidates,
      // sample values, ambiguous/unmapped warnings, per-entry accept) through
      // the SAME `stagedMappingProposal` machinery the deterministic
      // "Propose card mapping" button uses. Reused by IMPORT, never
      // redeclared — `terra/no-duplicate-schema-name` forbids a second
      // `MappingProposalResponseSchema`, and a hand-copied mirror would drift
      // from the classifier it must match field-for-field.
      mapping: MappingProposalResponseSchema.optional(),
    })
    .nullable(),
  sampleRowIndex: z.number().optional(),
  // Agent-driven wizard step navigation (plan 2026-08-05-003 U1), a top-level
  // sibling of `proposal` — navigation is a distinct concern from the
  // proposal/apply contract and must work even with `proposal: null` ("just
  // advance the human"). Note the semantic split from `step` above: `step`
  // is the cue's RENDER target (what the rail/wizard should display right
  // now), while `navigate.step` is the NAVIGATION target (where to move the
  // human's open tab) — deliberately a narrower 8-value wizard-only enum,
  // never the 13-value `step` union above.
  navigate: z
    .object({
      step: z.enum(WIZARD_STEP_IDS),
      confirmed: z.boolean(),
      baseRevision: z.number().optional(),
    })
    .optional(),
});
export type StudioCuePayload = z.infer<typeof StudioCuePayloadSchema>;

/**
 * Account/Contact notes invalidation. Ids only, never note text: the event is broadcast to every
 * connected app-home user (including ones outside the account's universe), and each client refetches
 * through its own scoped endpoints.
 */
export const FlowNotesChangedPayloadSchema = z.object({
  recordKind: NoteRecordKindSchema,
  recordId: z.string(),
  accountId: z.string(),
});

/**
 * How often the app-home stream handler writes a `ping` frame. The proxy
 * (Render) drops a connection that is silent for ~60-100s, so this stays well
 * under that. The client also reads it: a stream with no frame and no ping for
 * two intervals is treated as dead (see sseStore).
 */
export const APP_HOME_SSE_KEEPALIVE_MS = 25_000;

/**
 * Every event the app-home stream emits, keyed by event name. One runtime
 * object drives both sides: `SseRegistry.emit` is typed against it (a
 * misspelled event name or a wrong-shaped payload fails `tsc` at the emit
 * site) and sseStore picks its listener names and parse schemas from it. The
 * `ping` frame the stream handler writes on connect and every
 * `APP_HOME_SSE_KEEPALIVE_MS` is not emitted via the registry.
 */
export const SsePayloadSchemas = {
  toast: ToastPayloadSchema,
  'artifact:created': ArtifactCreatedPayloadSchema,
  'artifact:updated': ArtifactUpdatedPayloadSchema,
  'artifact:deleted': ArtifactDeletedPayloadSchema,
  invalidate: InvalidatePayloadSchema,
  'blueprint:draft:updated': BlueprintDraftUpdatedPayloadSchema,
  'studio:cue': StudioCuePayloadSchema,
  'flow:notes:changed': FlowNotesChangedPayloadSchema,
} as const;
export type SseEventName = keyof typeof SsePayloadSchemas;
export type SseEventData<E extends SseEventName> = z.infer<(typeof SsePayloadSchemas)[E]>;
